<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Resource Categories"
      subtitle="Manage resource category classifications"
      icon="fas fa-layer-group"
      gradient="blue"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="6"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableData.filter.search"
            placeholder="search category"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Status"
          label-for="filterActive"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableData.selected.active"
            input-id="filterActive"
            label="name"
            :options="activeOptions"
            clearable
            append-to-body
            :calculate-position="calculateDropPosition"
            :disabled="tableData.busy"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Action"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BButton
            size="sm"
            type="button"
            variant="primary"

            @click.prevent="createItem"
          >
            Add Category
          </BButton>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableData"
      :provider="tableProvider"
      :fields="tableData.fields"
      :filter="tableData.filter"
      :filter-on="tableData.filterOn"
      :sort-by="tableData.sortBy"
      :sort-desc="tableData.sortDesc"
      :sort-direction="tableData.sortDirection"
      :total-rows="tableData.totalRows"
      :current-page="tableData.currentPage"
      :per-page="tableData.perPage"
      :stacked="tableData.stacked"
      :busy="tableData.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableData.busy"
            @click.prevent="editItem(item)"
          >
            Edit
          </BButton>
          <BButton
            size="sm"
            variant="danger"
            :disabled="tableData.busy"
            @click.prevent="deleteItemConfirm(item)"
          >
            Delete
          </BButton>
        </div>
      </template>

      <template #cell(is_active)="{ item }">
        <BBadge :variant="item.is_active ? 'success' : 'secondary'">
          {{ item.is_active ? 'Active' : 'Inactive' }}
        </BBadge>
      </template>
    </CustomTable>

    <BModal
      ref="modalForm"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="state.editing ? 'Edit Category' : 'Add Category'"
    >
      <ValidationObserver ref="formData">
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="category_name"
                name="category name"
                rules="required|max:100"
              >
                <BFormGroup
                  label="Category Name"
                  label-for="fCategoryName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fCategoryName"
                    v-model="formData.category_name"
                    placeholder="enter category name"
                    maxlength="100"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="description"
                name="description"
                rules="max:5000"
              >
                <BFormGroup
                  label="Description"
                  label-for="fDescription"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormTextarea
                    id="fDescription"
                    v-model="formData.description"
                    placeholder="enter description"
                    rows="4"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <BFormGroup class="mb-2">
                <BFormCheckbox
                  id="is_active"
                  v-model="formData.is_active"
                  switch
                  value="1"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateForm"
        >
          {{ state.editing ? 'Update' : 'Create' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminResourceCategory } from '@/services'

export default {
  name: 'VAdminResourceCategories',

  components: { AdminPageHeader },

  mixins: [MxMisc, MxFormatter],

  middleware: ['auth', 'admin'],

  metaInfo: () => ({ title: 'Resource Categories' }),

  data () {
    return {
      state: { busy: false, editing: false },
      formData: {
        id: '',
        category_name: '',
        description: '',
        is_active: 1
      },
      activeOptions: [
        { id: 1, name: 'Active' },
        { id: 0, name: 'Inactive' }
      ],
      tableData: {
        busy: false,
        selected: {
          active: null
        },
        filter: {
          search: '',
          is_active: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'updated_at',
        sortDesc: true,
        sortDirection: 'desc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'category_name', class: 'min-w-175px' },
          { mobile: 2, key: 'description', class: 'min-w-250px' },
          { mobile: 3, key: 'is_active', class: 'text-center' },
          { mobile: 4, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  watch: {
    'tableData.selected.active' (active) {
      this.tableData.filter.is_active = active !== null && active !== undefined ? active.id : ''
    }
  },

  methods: {
    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableData.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        is_active: filter.is_active
      }

      return SAdminResourceCategory.getList(payload)
        .then(({ data: response }) => {
          const data = response?.data || response
          this.tableData.totalRows = data?.total_rows || 0
          return data?.items || []
        })
        .catch(() => {
          this.tableData.totalRows = 0
          return []
        })
        .finally(() => {
          this.tableData.busy = false
        })
    },

    createItem () {
      this.state.editing = false
      this.formData = { id: '', category_name: '', description: '', is_active: 1 }
      this.$refs.modalForm.show()
    },

    editItem (item) {
      this.state.editing = true
      this.formData = {
        id: item.id,
        category_name: item.category_name,
        description: item.description || '',
        is_active: item.is_active ? 1 : 0
      }
      this.$refs.modalForm.show()
    },

    async validateForm () {
      await this.$refs.formData.validate().then(async validForm => {
        if (!validForm) return this.swalInvalid()

        this.swalConfirm({
          html: this.state.editing ? '<h6>Update this category?</h6>' : '<h6>Create this category?</h6>',
          confirmButtonText: this.state.editing ? 'Update' : 'Create',
          preConfirm: async () => {
            this.state.busy = true

            const action = this.state.editing
              ? SAdminResourceCategory.update(this.formData.id, this.formData)
              : SAdminResourceCategory.store(this.formData)

            return action.then(() => {
              this.$refs.modalForm.hide()
              this.$refs.tableData.refetch()
              this.swalSuccess(`Category ${this.state.editing ? 'updated' : 'created'} successfully`)
            }).catch(({ message }) => {
              if (message?.errors) {
                this.$refs.formData.setErrors(message.errors)
              }
            }).finally(() => {
              this.state.busy = false
            })
          }
        })
      })
    },

    async deleteItemConfirm (item) {
      const result = await this.$swal({
        title: 'Delete category',
        text: `Are you sure you want to delete "${item.category_name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        this.state.busy = true
        SAdminResourceCategory.destroy(item.id).then(() => {
          this.swalSuccess('Category deleted successfully')
          this.$refs.tableData.refetch()
        }).catch(error => {
          this.swalError(error.response?.data?.message || 'Failed to delete category')
        }).finally(() => {
          this.state.busy = false
        })
      }
    }
  }
}
</script>
